import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileBarChart2, Download, Calendar, AlertTriangle, Wallet, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORTS = [
  { key: 'monthly-summary', label: 'Monthly Attendance Summary', icon: FileBarChart2,
    desc: 'Employee-wise Present/Absent/Leave/LOP/Payable for a month.', needs: ['month'] },
  { key: 'daily-register',  label: 'Daily Attendance Register', icon: Calendar,
    desc: 'Date-wise attendance for all active employees.', needs: ['date'] },
  { key: 'exceptions',      label: 'Exception Report', icon: AlertTriangle,
    desc: 'Missing biometric mapping, duplicate punches, leave-without-balance, absent→LOP.', needs: ['month'] },
  { key: 'payroll-input',   label: 'Payroll Attendance Input', icon: Wallet,
    desc: 'Final locked report consumed by payroll. Available only after lock.', needs: ['month'] },
];

export default function AttendanceReports() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [selected, setSelected] = useState(REPORTS[0].key);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        const cs = Array.isArray(data) ? data : (data.companies || []);
        setCompanies(cs);
        if (cs.length === 1) setCompanyId(cs[0].id);
      } catch { /* */ }
    })();
  }, []);

  const run = async () => {
    if (!companyId) { toast.error('Pick a company'); return; }
    setLoading(true); setRows([]);
    try {
      let url = `${API_URL}/attendance/v2/reports/${selected}?company_id=${companyId}`;
      const def = REPORTS.find(r => r.key === selected);
      if (def.needs.includes('month')) url += `&month=${month}`;
      if (def.needs.includes('date'))  url += `&date=${date}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setRows(data.rows || data.exceptions || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Report failed'); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    if (!rows.length) return;
    const displayKeys = Object.keys(rows[0]).filter(k => k !== '_id' && k !== 'internal_employee_id' && k !== 'tenant_id');
    const csv = [displayKeys.map(k => k === 'employee_id' ? 'Employee ID' : k.replace(/_/g, ' ')).join(','), ...rows.map(r => displayKeys.map(k => `"${(r[k] ?? '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${selected}_${month || date}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const def = REPORTS.find(r => r.key === selected);
  const displayKeys = rows.length ? Object.keys(rows[0]).filter(k => k !== '_id' && k !== 'internal_employee_id' && k !== 'tenant_id') : [];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="attendance-reports-page">
      <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2 mb-6">
        <FileBarChart2 className="text-blue-600" size={28} /> Attendance Reports
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        {REPORTS.map(r => {
          const Icon = r.icon; const active = selected === r.key;
          return (
            <button key={r.key} onClick={() => { setSelected(r.key); setRows([]); }}
              data-testid={`report-${r.key}`}
              className={`p-3 rounded-xl border text-left ${active ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'}`}>
              <Icon size={18} className={active ? 'text-blue-600' : 'text-slate-500'} />
              <div className="font-semibold mt-1 text-sm">{r.label}</div>
              <div className="text-xs text-slate-500 mt-1">{r.desc}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex items-center gap-3 flex-wrap">
        <select value={companyId} onChange={e => setCompanyId(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
          <option value="">— company —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {def?.needs.includes('month') && (
          <input type="month" value={month} onChange={e => setMonth(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
        )}
        {def?.needs.includes('date') && (
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
        )}
        <button onClick={run} disabled={loading} data-testid="report-run"
          className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-1">
          {loading ? <Loader2 size={14} className="animate-spin" /> : null} Run Report
        </button>
        {rows.length > 0 && (
          <button onClick={exportCSV} className="ml-auto px-4 py-2 rounded-lg bg-slate-100 text-sm flex items-center gap-1">
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>
          : rows.length === 0 ? <div className="p-10 text-center text-slate-500">Run report to populate this table.</div>
            : (
              <div className="overflow-x-auto max-h-[60vh]">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>{displayKeys.map(k => (
                      <th key={k} className="text-left px-3 py-2 capitalize whitespace-nowrap">{k === 'employee_id' ? 'Employee ID' : k.replace(/_/g, ' ')}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        {displayKeys.map(k => (
                          <td key={k} className={`px-3 py-1.5 whitespace-nowrap ${k === 'employee_id' || k === 'employee_code' ? 'font-mono font-medium text-slate-900' : ''}`}>{r[k] != null ? String(r[k]) : ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </div>
    </div>
  );
}
